import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SHOW_THRESHOLD_PX = 400;
const RING_VB = 60;
const RING_CX = RING_VB / 2;
const RING_R = 26;
const RING_C = 2 * Math.PI * RING_R;

export default function ScrollToTopButton() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const progressCircleRef = useRef<SVGCircleElement | null>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      const circle = progressCircleRef.current;
      if (circle) circle.setAttribute('stroke-dashoffset', String(RING_C * (1 - ratio)));
      setVisible(window.scrollY > SHOW_THRESHOLD_PX);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollUp = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <div
      className={`fixed bottom-5 end-5 sm:bottom-6 sm:end-6 z-[400] transition-[opacity,transform] duration-200 ease-out ${
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <div className="relative inline-flex transition-transform duration-150 ease-out active:scale-95">
        <svg
          viewBox={`0 0 ${RING_VB} ${RING_VB}`}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[68px] h-[68px] sm:w-[78px] sm:h-[78px] pointer-events-none"
          aria-hidden="true"
        >
          <circle
            cx={RING_CX}
            cy={RING_CX}
            r={RING_R}
            fill="none"
            stroke="var(--mint-deep)"
            strokeOpacity="0.18"
            strokeWidth="3"
          />
          <circle
            ref={progressCircleRef}
            cx={RING_CX}
            cy={RING_CX}
            r={RING_R}
            fill="none"
            stroke="var(--mint-deep)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={RING_C}
            strokeDashoffset={RING_C}
            transform={`rotate(-90 ${RING_CX} ${RING_CX})`}
          />
        </svg>
        <button
          type="button"
          onClick={scrollUp}
          aria-label={t('ui.scrollToTop')}
          title={t('ui.scrollToTop')}
          className="relative inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-mint border-2 border-ink rounded-full shadow-stamp text-ink transition-shadow duration-200 ease-out hover:shadow-stamp-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
            <path
              d="M6 14 L12 8 L18 14"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
